/**
 * usePWAInstall
 * =============
 * Hook que captura o evento beforeinstallprompt do browser (Android/Chrome/Edge)
 * e detecta o estado de instalação do PWA.
 *
 * Para iOS (Safari): não há prompt automático. O hook detecta o ambiente e
 * o componente UI mostra as instruções manuais ("Compartilhar → Adicionar à
 * Tela Inicial").
 */
import { useState, useEffect, useCallback } from 'react';

export function usePWAInstall() {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsInstalled(standalone);

    const ua = (navigator.userAgent || '').toLowerCase();
    const ios = /iphone|ipad|ipod/.test(ua) && !/crios|fxios|opios/.test(ua);
    setIsIOS(ios);

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!installPrompt) return false;
    try {
      installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstallPrompt(null);
        return true;
      }
      return false;
    } catch (e) {
      return false;
    }
  }, [installPrompt]);

  return { installPrompt, isInstalled, isIOS, install };
}

export default usePWAInstall;
